import { Container, Graphics } from "pixi.js";
import type { EffectChannel } from "../effects";
import {
  BOARD_PIXELS,
  FRICTION_SPARK_CAP,
  FRICTION_SPARK_INTERVAL_MS,
  FRICTION_SPARK_MIN_SPEED,
  TILE_SIZE,
} from "./constants";
type FrictionDragState = { axis: "row" | "col" | null; index: number; lastSparkAt: number };
export function createFrictionEffects({
  motionLayer,
  nextFrame,
  currentEffectScale,
  reducedMotion,
  host,
  isDisposed,
}: {
  motionLayer: Container;
  nextFrame: (callback: FrameRequestCallback) => number;
  currentEffectScale: (channel?: EffectChannel) => number;
  reducedMotion: boolean;
  host: HTMLDivElement;
  isDisposed: () => boolean;
}) {
  interface FrictionSpark {
    view: Graphics;
    x: number;
    y: number;
    vx: number;
    vy: number;
    bornAt: number;
    lifeMs: number;
    alpha: number;
  }

  let frictionSparks: FrictionSpark[] = [];

  let frictionFrameActive = false;

  let particleSequence = 0;

  const clearMotionEffects = () => {
    frictionSparks = [];
    frictionFrameActive = false;
    motionLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
  };

  const animateFrictionSparks = (now: number) => {
    if (isDisposed() || !frictionFrameActive) return;
    const alive: FrictionSpark[] = [];
    for (const spark of frictionSparks) {
      const progress = Math.min(1, (now - spark.bornAt) / spark.lifeMs);
      if (progress >= 1) {
        if (spark.view.parent === motionLayer) motionLayer.removeChild(spark.view);
        spark.view.destroy();
        continue;
      }
      const elapsed = (now - spark.bornAt) / 1000;
      spark.view.x = spark.x + spark.vx * elapsed;
      spark.view.y = spark.y + spark.vy * elapsed + 22 * elapsed * elapsed;
      spark.view.alpha = spark.alpha * (1 - progress);
      spark.view.scale.set(1 - progress * 0.55);
      alive.push(spark);
    }
    frictionSparks = alive;
    if (frictionSparks.length > 0) nextFrame(animateFrictionSparks);
    else frictionFrameActive = false;
  };

  const emitFrictionSparks = (
    state: FrictionDragState,
    point: { x: number; y: number },
    axisDelta: number,
    speed: number,
    now: number,
  ) => {
    const effectScale = currentEffectScale("particles");
    if (
      reducedMotion ||
      effectScale <= 0 ||
      !state.axis ||
      speed < FRICTION_SPARK_MIN_SPEED ||
      now - state.lastSparkAt < FRICTION_SPARK_INTERVAL_MS
    )
      return;
    const intensity = Math.max(0, Math.min(1, (speed - FRICTION_SPARK_MIN_SPEED) / 1.2));
    const sparkCap = Math.max(1, Math.round(FRICTION_SPARK_CAP * (0.5 + effectScale)));
    const desiredCount = Math.max(1, Math.round((1 + intensity * 3) * effectScale));
    const count = Math.min(sparkCap - frictionSparks.length, desiredCount);
    if (count <= 0) return;
    state.lastSparkAt = now;
    const direction = axisDelta < 0 ? -1 : 1;
    for (let piece = 0; piece < count; piece++) {
      const sequence = particleSequence++;
      const variation = (sequence % 5) - 2;
      const edgeSign = sequence % 2 === 0 ? -1 : 1;
      const view = new Graphics();
      view
        .rect(0, 0, sequence % 3 === 0 ? 3 : 2, 2)
        .fill({ color: sequence % 4 === 0 ? "#f0d39a" : "#c49a58" });
      let x = Math.max(2, Math.min(BOARD_PIXELS - 4, point.x));
      let y = Math.max(2, Math.min(BOARD_PIXELS - 4, point.y));
      let vx = 0;
      let vy = 0;
      if (state.axis === "row") {
        x += variation;
        y = state.index * TILE_SIZE + (edgeSign < 0 ? 2 : TILE_SIZE - 3);
        vx = -direction * (22 + intensity * 24) + variation * 4;
        vy = edgeSign * (18 + intensity * 20);
      } else {
        x = state.index * TILE_SIZE + (edgeSign < 0 ? 2 : TILE_SIZE - 3);
        y += variation;
        vx = edgeSign * (18 + intensity * 20);
        vy = -direction * (22 + intensity * 24) + variation * 4;
      }
      view.x = x;
      view.y = y;
      motionLayer.addChild(view);
      frictionSparks.push({
        view,
        x,
        y,
        vx,
        vy,
        bornAt: now,
        lifeMs: 100 + intensity * 80,
        alpha: (0.5 + intensity * 0.5) * effectScale,
      });
    }
    host.dataset.frictionSparkCount = String(
      Number(host.dataset.frictionSparkCount ?? "0") + count,
    );
    if (!frictionFrameActive) {
      frictionFrameActive = true;
      nextFrame(animateFrictionSparks);
    }
  };
  return { clearMotionEffects, emitFrictionSparks };
}
